import React, {Component} from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';

import {Divider, TextField, Typography, Tooltip, IconButton} from '@material-ui/core';
import {TableRow, TableCell} from '@material-ui/core';
import Button from '../Button';
import ConfirmButton from '../ConfirmButton';
import {Card, CardContent, CardActions} from '@material-ui/core';
import { Delete, Help } from '@material-ui/icons';
import grey from '@material-ui/core/colors/grey';
import PaginatedList from '../PaginatedList';
import { FormattedMessage } from 'react-intl';
import DomainRegistrationButton from '../domains/DomainRegistrationButton';

/**
 * View of the paginated list of invitations for a subscription
 * Allows to invite more users by entering a comma separated list of emails
 */

const styles = theme => ({
  form: theme.form,
  empty: {
    visibility:"hidden"
  },
  tools: {
    textAlign: "right"
  },
  iconButton: {
    color: grey[600],
  },
  full: {
    overflow: 'auto',
  },
  button: {
    color: "#6AA031"
  },
  updateInfo: {
    backgroundColor: grey[200],
    height: 180,
    padding: 24,
    display:'flex', 
    justifyContent:'center'
  },
  center: {
    marginTop: 8,
    display:'flex', 
    justifyContent: 'center',
  },
  tooltip: {
    color: grey[600],
  }
});

// eslint-disable-next-line
const DOMAIN_REGEX = /^([a-z0-9][a-z0-9-_]*\.)+[a-z0-9]*[a-z0-9-_]*[[a-z0-9]{2,}$/i;


class DomainList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      inviting: false,
      domain: {
        value: props.principalDomain ? props.principalDomain : "",
        errors: []
      }
    };
  }

  /**
   * Input onchange callback
   * Validates emails and sets errors if needed
   * @param {Event} e 
   */
  handleDomainChange(e) {
    const value = e.target.value;
    const errors = [];
    
    if(!DOMAIN_REGEX.test(value)) {
      errors.push({
        message: `Invalid domain: ${value}`,
        domain: value,
      });
    }

    this.setState({
      domain: {value, errors}
    });
  }

  /** 
   * Resets the invitation form to its initial state (closed, empty)
  */
  reset(){
    this.setState({
      inviting: false,
      domain: {
        value: this.props.principalDomain? this.props.principalDomain : "",
        errors: []
      }
    });
  }

  /** 
   * Cancel button callback
   * Resets the form and closes the form
  */
  handleCancel() {
    this.reset();
  }

  /** 
   * Called when the user hits "Regiter Domain"
   * Posts the form and refreshes the view on success
  */
  handleRegisterDomain(domainValue) {
    const {handleInviteDomains} = this.props;

    handleInviteDomains(domainValue)
      .then(action=>{
        this.reset();
        this.refresh();
        return action;
      });
      
  }

  /**
   * Cancel invitation callback
   * Deletes the invitation and refreshes the view
   * @param {Object} invitation 
   */
  handleRemoveDomainInvitation(domain) {
    const {
      handleRemoveDomainInvitation,
    } = this.props;

    handleRemoveDomainInvitation(domain)
      .then(action => this.refresh());
  }

  /** 
   * Refreshes the list of invitations by re-loading current page from the API
  */
  refresh() {
    const {
      pagination,
      handleLoadPage
    } = this.props;

    if( pagination ) {
      // FIXME
      handleLoadPage(pagination.limit, pagination.currentToken);
    }
  }

  getErrorCardContent(subscription, subscriptionDomains, navigateToSubscriptionEdit, navigateToSupport, principalDomain, classes) {
  
    const domainInvitationSettings = subscription.plan.domainInvitationSettings;
    const ownerDomainAlreadyInvited = subscription.ownerDomainAlreadyInvited;

    if(ownerDomainAlreadyInvited && (!subscriptionDomains || !subscriptionDomains.totalCount || subscriptionDomains.totalCount === 0)) {
      return {
        message: <FormattedMessage id='subscription.domains.register.message.already' values={{domain: principalDomain}} />, 
        buttonLabel: <FormattedMessage id='subscription.domains.update.button.contactUs' />, 
        buttonAction: () => navigateToSupport()
      };
    }
  
    if(!domainInvitationSettings) {
      return {
        message: 
          <div style={{display:'flex', flexDirection:'row'}}>
            <Typography variant='body2' color='textSecondary' style={{flexGrow:1, alignSelf:'center'}}>
              <FormattedMessage id='subscription.domains.update.text.updateSubscription' />
            </Typography>
            <Tooltip title="learn more" placement="bottom-end">
              <IconButton
                href="https://sites.google.com/altirnao.com/aolm-faq/my-account-faq#h.p_1jC6vms-Hl2g"
                target="_blank">
                <Help fontSize='small' className={classes.tooltip}/>
              </IconButton>
            </Tooltip>
          </div>, 
        buttonLabel: <FormattedMessage id='subscription.domains.update.button.updateSubscription' />, 
        buttonAction: () => navigateToSubscriptionEdit(subscription.id)
      };
    }
  }

  render() {
    const {
      pagination, 
      subscription, 
      classes, 
      handleLoadPage,
      navigateToSubscriptionEdit,
      navigateToSupport,
      productName,
      principalDomain,
    } = this.props;
    const {domain} = this.state;

    const domainInvitationSettings = subscription.plan.domainInvitationSettings;
    const hasDomainErrors = domain.errors.length > 0;
    const hasBackendError = subscription.errors[0] && !!subscription.errors[0].message;    
    const domainInputIsEditable = domainInvitationSettings && !domainInvitationSettings.onlyOwnerDomain;
    const addButtonEnabled = !hasDomainErrors && !!domain.value;

    const errorCardContent = this.getErrorCardContent(subscription, pagination, navigateToSubscriptionEdit, navigateToSupport, principalDomain, classes);

    return (
    <div>
      
      <Card className={classes.full}>
        { errorCardContent 
          ? <div className={classes.updateInfo}>
          <CardContent >
            <Typography variant='body2' color='textSecondary'>
              {errorCardContent.message} 
            </Typography>
            <div className={classes.center}>
              <Button
                variant="text"
                size="small"
                color="primary"
                onClick={errorCardContent.buttonAction}>
                  {errorCardContent.buttonLabel} 
              </Button>
            </div>
          </CardContent>
        </div>
        : pagination && (pagination.totalCount<domainInvitationSettings.maxDomainsCount) &&
            <div className={classes.form}>
              <Divider />
              <CardContent>
                <div style={{marginBottom:8}}>
                  {hasDomainErrors && 
                    <Typography variant='body2' color='error'>
                      {domain.errors[0].message}
                    </Typography> }
                  {!(hasDomainErrors || hasBackendError) && 
                    <div style={{display:'flex', flexDirection:'row'}}>
                      <Typography variant='body2' color='textSecondary' style={{flexGrow:1, alignSelf:'center'}}>
                        <FormattedMessage id='subscription.domains.register.message.general' values={{productName}} />
                      </Typography>
                      <Tooltip title="learn more" placement="bottom-end">
                        <IconButton
                          href="https://sites.google.com/altirnao.com/aolm-faq/my-account-faq#h.p_1jC6vms-Hl2g"
                          target="_blank">
                          <Help fontSize='small' className={classes.tooltip}/>
                        </IconButton>
                      </Tooltip>
                    </div> }
                  {hasBackendError && 
                    <Typography variant='body2' color='error'>
                      {subscription.errors[0].message}
                    </Typography> }
                </div>
                <TextField 
                  required
                  label="Domain name"
                  value={domain.value} 
                  onChange={e=>this.handleDomainChange(e)} 
                  error={hasDomainErrors}
                  disabled={!domainInputIsEditable}
                  fullWidth
                />
              </CardContent>
              
              <CardActions>
                <DomainRegistrationButton 
                  handleRegisterDomain={() => this.handleRegisterDomain(domain.value)}
                  domain={domain.value}
                  productName={productName}
                  disabled={!addButtonEnabled}
                />
              </CardActions>
            </div>
        }
        
        <Divider />
        <PaginatedList
          changeKey={subscription.id}                                                                                                                                                                                                                                   
          pagination={pagination}
          isDomain={true}
          handleLoadPage={handleLoadPage}
          columns={[
            <FormattedMessage id='subscription.domains.column.domainName' />,
            <FormattedMessage id='subscription.domains.column.licencesNumber' />,
            ""
          ]}
          Row={(item)=>(
            <TableRow key={item.id} className={classes.form}>
              <TableCell>
                  {item.domain}
              </TableCell>
              <TableCell>
                  {item.licencesCount}
              </TableCell>
              <TableCell className={classes.tools}>
                  <ConfirmButton
                    iconButton
                    size="small"
                    color="primary"
                    onClick={()=>this.handleRemoveDomainInvitation(item)}
                    title={<FormattedMessage id="subscription.domains.confirmation.removeDomain.title" values={{domainName: item.domain}} />}
                    message={<FormattedMessage id="subscription.domains.confirmation.removeDomain.description" values={{domainName: item.domain}} />}
                    yes={<FormattedMessage id='subscription.domains.confirmation.removeDomain.button.confirm' />}
                    no={<FormattedMessage id='subscription.domains.confirmation.removeDomain.button.cancel' />}
                    >
                    <Delete className={classes.iconButton} />
                  </ConfirmButton>
              </TableCell>
            </TableRow>
          )}
          /> 
        </Card>
      </div>
    )
  }
}

DomainList.propTypes = {
  pagination: PropTypes.object, 
  subscription: PropTypes.object.isRequired, 
  classes: PropTypes.object.isRequired, 
  handleLoadPage: PropTypes.func.isRequired,
}

export default withStyles(styles)(
  DomainList
);


// WEBPACK FOOTER //
// ./src/components/subscriptions/DomainList.js